import * as styles from '../FirstResponder.module.css';
import AuditTable from '../Connected/AuditTable';
import {
  LiveAuditRow,
  LivePage,
  LivePilotPolicy,
  LiveSettingsDraft,
} from '../LivePreview/liveViewModel';

/*
 * Canonical presentation component (GRAVY-10373 convergence, Phase 1 extraction, Phase 5
 * de-duplication).
 *
 * Extracted verbatim from LiveConsole.tsx's Pilot settings <details> section: derived settings
 * display, the audited-edit explainer, the "Edit pilot settings" button, and the change-audit
 * table (now delegated to Connected/AuditTable.tsx, which was already a byte-identical extraction
 * of this same markup — importing it instead of re-inlining it closes the one remaining
 * "two independent versions of the same presentation" gap this convergence's task explicitly
 * forbids). Deliberately does NOT render the settings editor modal itself (SettingsEditor in the
 * preview, ConnectedPolicyEditor for real data, per Phase 5) — that stays the caller's choice,
 * exactly as today: this component only ever asks its caller to open one, via onOpenEditor.
 */

// The two timing settings read as one sum to a newcomer ("wait 15, then wait 90?"). They are two
// separate tests made at the same moment, and the help line under each says so. The editors carry
// the same two sentences so the display and the form never explain the numbers differently.
export const WAIT_HELP =
  'Starts when the first worker notification goes out. First Responder looks at the job after this wait, so the normal notification flow gets its chance first.';
export const LEAD_HELP =
  'Measured from the shift start. If the shift is closer than this when First Responder looks, it stands down. Not added to the wait above: the two are separate tests made at the same moment.';

// Settings display entries are DERIVED from the active policy object — the single source. There
// is no second prose copy to fall out of sync.
// The policy version on the wire is the activation token, 64 hex characters (GRAVY-10816 walk, W3).
// An operator compares versions by eye, so the panel shows the git-style short form; the full token
// stays on the wire and in the audit trail.
const shortToken = (v: string): string =>
  v.length > 12 ? `${v.slice(0, 8)}…` : v;

const settingsEntries = (
  p: LivePilotPolicy,
  draft: LiveSettingsDraft | null,
) => [
  ['Clients', p.clients.map((c) => c.name).join(', ')],
  [
    'Wait before evaluation',
    `${p.waitBeforeEvaluationMinutes} minutes`,
    WAIT_HELP,
    'wait',
  ],
  ['Minimum lead time', `${p.minLeadMinutes} minutes`, LEAD_HELP, 'lead'],
  ['Max offers per job', String(p.maxOffersPerJob)],
  ['Distance limit', `${p.distanceLimitMiles} miles`],
  [
    'Experiment allocation',
    `${p.treatmentPercent}% Treatment · ${100 - p.treatmentPercent}% Control`,
  ],
  [
    'Policy version',
    `${shortToken(p.version)}${draft ? ' · draft pending' : ''}`,
  ],
];

type Props = {
  active: LivePilotPolicy;
  draft: LiveSettingsDraft | null;
  auditPage: LivePage<LiveAuditRow>;
  onAuditPageChange: (page: number) => void;
  // GRAVY-10373 F8: a failed audit page fetch. Optional and null in the preview (whose audit page
  // never fails); only the real connected container supplies it.
  auditError?: { message: string; onRetry: () => void } | null;
  controlsDisabled: boolean;
  // Why editing is locked, shown under the disabled button. Defaults to the safety reason so a
  // caller that only knows about the safety lock keeps today's copy.
  controlsDisabledReason?: string;
  onOpenEditor: () => void;
};

const PilotSettingsSection = ({
  active,
  draft,
  auditPage,
  onAuditPageChange,
  auditError,
  controlsDisabled,
  controlsDisabledReason = 'Editing is disabled until the safety status is clear.',
  onOpenEditor,
}: Props) => (
  <details className={styles.collapse} data-testid="flp-settings">
    <summary>Pilot settings</summary>
    <div
      className={styles.section}
      style={{ borderTop: '1px solid var(--n-lightest)' }}
    >
      <div className={`${styles.sig} ${styles.sigFlush}`}>
        {settingsEntries(active, draft).map(([label, value, help, id]) => (
          <div key={label}>
            <div className={styles.k}>{label}</div>
            <div className={styles.v}>{value}</div>
            {help && (
              <div
                className={`${styles.muted} ${styles.small}`}
                style={{ marginTop: 4, maxWidth: '38ch' }}
                data-testid={`flp-setting-help-${id}`}
              >
                {help}
              </div>
            )}
          </div>
        ))}
      </div>
      <p
        className={`${styles.muted} ${styles.small}`}
        style={{ margin: '12px 0 0' }}
        data-testid="flp-allocation-help"
      >
        Experiment allocation is a deterministic job-level assignment persisted
        with the experiment version. Treatment and control both evaluate and
        record a comparable shortlist; control never creates offers or contacts
        workers. In SHADOW neither arm acts; in a future authorized LIVE mode
        treatment may act while control remains watch-only. Never a percentage
        of workers or clients.
      </p>
      <div style={{ marginTop: 12 }}>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnOutline}`}
          data-testid="flp-edit-settings"
          disabled={controlsDisabled}
          onClick={onOpenEditor}
        >
          Edit pilot settings
        </button>
      </div>
      {/* GRAVY-10373 Task 8: the settings a safety failure locks stay VISIBLE — only editing them
          is blocked, and a disabled button with no reason next to it is just a dead control. Same
          markup and class as ConnectedPolicyEditor's own flp-activate-blocked notice. */}
      {controlsDisabled && (
        <div
          className={`${styles.muted} ${styles.small}`}
          style={{ marginTop: 8 }}
          data-testid="flp-settings-blocked"
        >
          {controlsDisabledReason}
        </div>
      )}
      <p
        className={`${styles.muted} ${styles.small}`}
        style={{ margin: '12px 0 0' }}
      >
        The active version is immutable — edits create a draft, activation is a
        separate audited step (only while First Responder is OFF), and changes
        never rewrite recorded runs.
      </p>

      <div style={{ marginTop: 16 }}>
        {/* F8: a refused audit page change used to do nothing at all — the rows just stayed and
            the click read as dead. The notice sits directly above the table it describes, with a
            Retry for that same page; the previously-loaded rows stay put underneath it. Same
            markup as JobsCard's own inline notice, so both failures read identically. */}
        {auditError && (
          <div data-testid="flp-audit-load-error" role="alert">
            <div className={styles.formErrors} style={{ marginBottom: 8 }}>
              {auditError.message}
              <button
                type="button"
                className={`${styles.btn} ${styles.btnOutline} ${styles.btnSm}`}
                style={{ marginLeft: 8 }}
                onClick={auditError.onRetry}
              >
                Retry
              </button>
            </div>
          </div>
        )}
        <AuditTable page={auditPage} onPageChange={onAuditPageChange} />
      </div>
    </div>
  </details>
);

export default PilotSettingsSection;
